import React, { useCallback, useEffect } from 'react';
import type * as CSS from 'csstype';
import './entity-locked.css';
import { EditorService } from '../../../services/editor';
import {
	CanvasEntityDisabledReason,
	lowercaseFirstLetter,
	READABLE_ENTITY_TYPES,
	UserManagedEntityState,
} from '@elyx-code/project-logic-tree';
import {
	useCanvasPopup,
} from '../../../services/editor/CanvasPopup';
import CanvasPopupBaseComponent from '../../../lib/popup/CanvasPopupBaseComponent';
import { CanvasEntityElement } from '../../../services/editor/CanvasElement';

export interface IEntityLockedIconProps {
	entity: UserManagedEntityState;
	project: EditorService;
	idSuffix?: string;
	canvasElement: CanvasEntityElement;
	parentDisabledReasons: CanvasEntityDisabledReason[];
	style?: CSS.Properties;
	[key: string]: any;
}

export default function EntityLockedIconMenu(props: IEntityLockedIconProps) {
	let message = '';

	if (!props.entity.deletable && !props.entity.editable) {
		message =
			'This ' +
			lowercaseFirstLetter(READABLE_ENTITY_TYPES[props.entity.type].singular) +
			' cannot be deleted or edited. Because the system need to refer to it over time.';
	} else if (!props.entity.editable) {
		message =
			'This ' +
			lowercaseFirstLetter(READABLE_ENTITY_TYPES[props.entity.type].singular) +
			' cannot be edited. Because the system need to refer to it over time.';
	} else if (!props.entity.deletable) {
		message =
			'This ' +
			lowercaseFirstLetter(READABLE_ENTITY_TYPES[props.entity.type].singular) +
			' cannot be deleted. Because the system need to refer to it over time.';
	}

	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				id={
					'entity-locked-icon--' +
					props.entity.id +
					'--popup--body' +
					(props.idSuffix || '')
				}
				containerClasses={['tooltip', 'entity-locked-menu']}
			>
				{message}
			</CanvasPopupBaseComponent>
		),
		[props.entity, props.idSuffix, message]
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'entity-locked-icon--' +
				props.entity.id +
				'--popup' +
				(props.idSuffix || ''),
			anchorQuerySelector:
				'#entity-locked-icon--' + props.entity.id + (props.idSuffix || ''),
			arrowClasses: ['tooltip-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject,
			placement: 'top',
			arrowContainerSelector:
				'#entity-locked-icon--' +
				props.entity.id +
				'--popup--body' +
				(props.idSuffix || ''),
		},
		memoizedCanvasPopupBody,
	)
		.setupHover({
			closeDelay: 80,
			openDelay: 600,
		})
		.onBeforeShow(() => {
			// If the canvas element is currently being dragged,
			// Or it is currently selecting an area, then do not show the popup
			if (
				props.project.canvas?.isSelecting ||
				props.project.canvas?.isMovingObjects ||
				!!props.parentDisabledReasons.length
			) {
				return false;
			}

			return true;
		});

	// useEffect(() => {
	// 	return () => {
	// 		canvasPopup.destroy();
	// 	};
	// }, []);

	const {
		canvasElement,
		onRender,
		project,
		entity,
		style,
		idSuffix,
		parentDisabledReasons,
		...otherProps
	} = props;

	return !props.entity.deletable || !props.entity.editable ? (
		<i
			ref={canvasPopup.anchorRef}
			{...otherProps}
			style={style}
			id={'entity-locked-icon--' + entity.id + (idSuffix || '')}
			className={[
				'fa-regular fa-lock',
				'entity-locked-icon',
				...(!!parentDisabledReasons.length ? ['disabled'] : []),
			].join(' ')}
		/>
	) : null;
}
